import type { KnowledgeAnswer, KnowledgeContext, KnowledgeLink, KnowledgeQuery } from './knowledgeRegistry';
import { containsFuzzyPhrase, tokenize } from './fuzzyMatch';

/**
 * Explains what a buyer can actually do to a booking once it exists — cancel
 * it, end a month-to-month one, or change its dates — none of which the
 * assistant could answer before this skill existed.
 *
 * ## Why this needed writing out rather than a generic "go manage it" answer
 *
 * The three questions this covers have genuinely different answers, and
 * getting them right matters more than being terse:
 *
 * - **Cancelling** a fixed-date booking and **ending** a month-to-month one are
 *   different actions in the UI (`Cancel Booking` vs `End Monthly Booking`),
 *   so a buyer who cancels a monthly booking by mistake would not get the
 *   ending they wanted.
 * - **Changing dates** has no self-serve control at all — a month-to-month
 *   booking already runs indefinitely until ended, and a fixed-date booking's
 *   dates cannot be edited in place. Saying otherwise would send someone
 *   looking for a button that does not exist.
 *
 * A single generic "manage your booking from the Bookings page" answer would
 * be true but would not tell a buyer asking to extend a booking that there is
 * nothing to extend, which is the actual answer they need.
 */

const BOOKINGS_LINK: KnowledgeLink = { label: 'Go to Bookings', path: '/bookings' };

const CANCEL_WORDS = ['cancel', 'canceling', 'cancelling', 'end', 'ending', 'stop', 'terminate'];
const CHANGE_WORDS = [
  'extend',
  'extending',
  'longer',
  'change',
  'changing',
  'adjust',
  'adjusting',
  'modify',
  'edit',
  'editing',
  'date',
  'dates',
  'renew',
];

const hasAny = (tokens: string[], words: string[]): boolean =>
  words.some((word) => containsFuzzyPhrase(tokens, tokenize(word)));

const CANCEL_TEXT =
  'To cancel a booking, go to **Bookings**, open the booking, and click **Cancel Booking**. ' +
  'If it is a month-to-month booking, use **End Monthly Booking** instead — that stops it from ' +
  'renewing going forward rather than cancelling it outright. Either way you will be asked to confirm ' +
  'before it takes effect.';

const CHANGE_TEXT =
  'There is no self-serve way to change a booking\u2019s dates once it is made. A month-to-month ' +
  'booking already runs indefinitely — there is nothing to extend, it simply continues until you ' +
  '**End Monthly Booking**. A fixed-date booking\u2019s dates are locked in; to add more time you would ' +
  'need to place a new booking for the extra days, or contact the supplier directly if you want to ' +
  'ask about extending the existing one.';

const answer = ({ message }: KnowledgeContext): Promise<KnowledgeAnswer> => {
  const tokens = tokenize(message);
  const wantsCancel = hasAny(tokens, CANCEL_WORDS);
  const wantsChange = hasAny(tokens, CHANGE_WORDS);

  if (wantsCancel && !wantsChange) {
    return Promise.resolve({ text: CANCEL_TEXT, link: BOOKINGS_LINK });
  }

  if (wantsChange && !wantsCancel) {
    return Promise.resolve({ text: CHANGE_TEXT, link: BOOKINGS_LINK });
  }

  return Promise.resolve({
    text: `${CANCEL_TEXT}\n\n${CHANGE_TEXT}`,
    link: BOOKINGS_LINK,
  });
};

export const bookingLifecycleQuery: KnowledgeQuery = {
  id: 'buyerBookingLifecycle',
  audiences: ['buyer'],
  synonyms: [
    'how do i cancel my booking',
    'how do i cancel a booking',
    'how can i cancel my booking',
    'how can i cancel a booking',
    'cancel my booking',
    'cancel a booking',
    'how do i end my booking',
    'how do i end a monthly booking',
    'end my monthly booking',
    'how do i extend my booking',
    'how can i extend my booking',
    'can i extend my booking',
    'extend my booking',
    'how do i change my booking dates',
    'how do i change the dates on my booking',
    'change my booking dates',
    'can i change my booking dates',
    'how do i modify my booking',
  ],
  requires: [
    ['booking', 'bookings', 'reservation'],
    [...CANCEL_WORDS, ...CHANGE_WORDS],
  ],
  example: 'how do I cancel my booking',
  description:
    'Explains what a buyer can do to change an existing booking: cancel a fixed-date booking, ' +
    'end a month-to-month one, or change its dates. There is no API to call \u2014 this is process ' +
    'guidance, distinct from the payment-failure and decline-reason lookups, which diagnose a ' +
    'specific booking\u2019s status rather than explain what actions are available.',
  answer,
};

export const __testing = {
  hasAny,
  CANCEL_WORDS,
  CHANGE_WORDS,
};
